<template>
	<tw-modal
		title="Latest Webinar"
		modal-max-width="max-w-md"
		ref="modal">
		<div
			v-if="loading"
			class="py-6 text-sm text-gray-500">
			Loading...
		</div>
		<div
			v-else-if="error || !webinar"
			class="py-6 text-sm text-gray-500">
			Unable to load webinar details right now.
		</div>
		<div
			v-else
			class="py-4">
			<a
				:href="webinar.permalink"
				target="_blank"
				rel="noopener noreferrer"
				class="inline-flex items-center gap-1 text-base font-semibold text-indigo-600 hover:text-indigo-800">
				{{ webinar.title }}
				<ArrowTopRightOnSquareIcon class="h-4 w-4 shrink-0" />
			</a>

			<p class="mt-2 text-sm text-gray-600">{{ webinar.description }}</p>

			<div class="mt-4 grid grid-cols-3 gap-4 border-t border-gray-100 pt-4">
				<div>
					<span class="block text-xs font-semibold uppercase tracking-wide text-gray-400">When</span>
					<span class="text-sm text-gray-900">{{ webinar.date }}</span>
				</div>
				<div v-if="webinar.speaker">
					<span class="block text-xs font-semibold uppercase tracking-wide text-gray-400">Speaker</span>
					<span class="text-sm text-gray-900">{{ webinar.speaker }}</span>
				</div>
				<div v-if="webinar.topics">
					<span class="block text-xs font-semibold uppercase tracking-wide text-gray-400">Topics</span>
					<span class="text-sm text-gray-900">{{ webinar.topics }}</span>
				</div>
			</div>

			<a
				v-if="webinar.replayUrl"
				:href="webinar.replayUrl"
				target="_blank"
				rel="noopener noreferrer"
				class="mt-6 flex w-full items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500">
				Watch Replay
			</a>
		</div>
	</tw-modal>
</template>

<script lang="ts">
	export default { name: "WebinarModal" } // name the component
</script>

<script lang="ts" setup>
	import {
		ArrowTopRightOnSquareIcon
	} from "@heroicons/vue/24/outline"

	import {
		defineExpose,
		ref
	} from "vue"

	import api from "@/utilities/api"

	const dateFormatter = new Intl.DateTimeFormat("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric"
	})

	const modal = ref<{open: boolean}>({open: false})
	const loading = ref(false)
	const error = ref(false)
	const webinar = ref<{
		title: string,
		description: string,
		date: string,
		permalink: string,
		speaker: string | null,
		topics: string | null,
		replayUrl: string | null
	} | null>(null)

	// reveal the modal and (re)fetch the most recently published webinar entry
	function open() {
		modal.value.open = true
		getLatestWebinar()
	}

	// fetch the most recent webinar entry from the backend and format it for display
	async function getLatestWebinar() {
		loading.value = true
		error.value = false

		try {
			const response = await api("get", "fmDataAPI", "/webinar-latest")

			if (!response.webinar) {
				error.value = true
				return
			}

			webinar.value = {
				...response.webinar,
				date: dateFormatter.format(new Date(response.webinar.date))
			}
		}
		catch(e) {
			error.value = true
		}
		finally {
			loading.value = false
		}
	}

	defineExpose({
		open
	})
</script>
